/** Copy shared by both Dashboard post-checkout offers. */

export const DASHBOARD_INCLUDED_FEATURES = [
  'Live options flow',
  'Dealer positioning & GEX',
  'Dark pool',
  'Signals & alerts',
  'Charting',
  'Research & payoff modeling',
];

/** One check line in the "What's included" block: a bold lead and its plain tail. */
export interface DashboardOfferLine {
  lead: string;
  rest?: string;
}

export const DASHBOARD_ANNUAL_INCLUDED_LINES: DashboardOfferLine[] = [
  { lead: 'Every feature we ship,', rest: 'included at this price.' },
  { lead: 'Even features we add to higher tiers', rest: 'later.' },
  { lead: 'We ship every week.', rest: 'You never pay more for any of it.' },
];

export const DASHBOARD_LIFETIME_INCLUDED_LINES: DashboardOfferLine[] = [
  { lead: 'Every feature we release,', rest: 'at no charge, forever.' },
  { lead: 'Even features we add to higher tiers.' },
  { lead: 'We ship every week.', rest: 'Lifetime members get all of it and never pay again.' },
];

// Real customer quotes from the 2026-07-16 quote mine, approved for publication by the owner and
// already used on the public landing page. Reproduced verbatim; no performance claims.
export const DASHBOARD_ANNUAL_OFFER_QUOTE = {
  author: 'Bryan B., InsiderFinance member',
  text: 'InsiderFinance is a game changer. The real-time option flow in combination with the visualizations help me identify quickly where the smart money is flowing to and what side of the contracts they are betting big money on.',
};

export const DASHBOARD_LIFETIME_OFFER_QUOTE = {
  author: 'Robert, truenorthtradr',
  text: 'I’ve had an easier time plotting my structural levels with real time GEX levels. Way more decisive and objective. Less likely to marry a bias. Top top platform.',
};

export interface DashboardOfferDeclineCopy {
  body: string;
  keepLabel: string;
  leaveLabel: string;
  title: string;
}

interface AnnualDeclineParams {
  /** "$75/month" or "$195 every 3 months"; null when the entry plan is unrecognised. */
  entryRate: string | null;
  monthlyPlan: string;
  offer: string;
  savingsVsEntry: string | null;
  savingsVsMonthlyPlan: string;
  savingsVsRegularAnnual: string;
}

/**
 * The one-step confirm behind "No thanks" on the annual offer. It restates the loss, says the
 * price is never offered again, and puts the offer above the leave link. Pure so the copy can be
 * asserted without a portal.
 */
export const getDashboardAnnualDeclineCopy = ({
  entryRate,
  monthlyPlan,
  offer,
  savingsVsEntry,
  savingsVsMonthlyPlan,
  savingsVsRegularAnnual,
}: AnnualDeclineParams): DashboardOfferDeclineCopy => ({
  body:
    entryRate && savingsVsEntry
      ? `You’ll keep paying ${entryRate}. That’s ${savingsVsEntry} more every year for the exact same Dashboard. ${offer} will never be offered again. Not in your settings. Not by email. Not at renewal.`
      : `You’ll pay ${savingsVsRegularAnnual} more every year than this price for the exact same Dashboard, and ${savingsVsMonthlyPlan} more than this on the ${monthlyPlan}/month plan. ${offer} will never be offered again. Not in your settings. Not by email. Not at renewal.`,
  keepLabel: `Lock in ${offer}/year`,
  leaveLabel: entryRate
    ? `Continue to the Dashboard at ${entryRate}`
    : 'Continue to the Dashboard without it',
  title: 'Leave without the discount?',
});

interface LifetimeDeclineParams {
  entryKnown: boolean;
  later: string;
  lifetime: string;
  renewal: string;
}

export const getDashboardLifetimeDeclineCopy = ({
  entryKnown,
  later,
  lifetime,
  renewal,
}: LifetimeDeclineParams): DashboardOfferDeclineCopy => ({
  body: entryKnown
    ? `Lifetime is ${lifetime} right now and ${later} after this page. Your ${renewal} renewal stays on, every year. ${lifetime} will never be offered again.`
    : `Lifetime is ${lifetime} right now and ${later} after this page. Your renewals stay on. ${lifetime} will never be offered again.`,
  keepLabel: `Get Lifetime for ${lifetime}`,
  leaveLabel: 'Continue to the Dashboard, keep renewing',
  title: 'Leave and pay double later?',
});
